import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Linking,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import Images from "@/assets/images";
import Header from "@/components/ui/Header";
import Icons from "@/assets/icons";
import * as StoreReview from "expo-store-review";
import { router } from "expo-router";
import Constants from "expo-constants";
import FBTrackingService from "@/services/fbTrackingService";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { StyleSheet } from "@/utils/appStyleSheet";
import useUserStore from "@/store/user";

const reviews = [
  {
    avatar: Images.avatarReview1, // Placeholder, bạn thay sau
    text: "Sparrow finds forgotten money, cancels old subscriptions. Super helpful, easy to use!",
    stars: 5,
    opacity: 1,
  },
  {
    avatar: Images.avatarReview2, // Placeholder
    text: "Amazing app! Recovers hidden funds, helps with class actions. A must-have.",
    stars: 5,
    opacity: 1,
  },
  // {
  //   avatar: Images.avatarReview3, // Placeholder
  //   text: "Sparrow is a gem. Tracks down refunds, gets you owed settlement money.",
  //   stars: 5,
  //   opacity: 1,
  // },
];

const RateUsScreen = () => {
  const { isFreePlan } = useUserStore();
  const [nativeAvailable, setNativeAvailable] = useState<boolean | null>(null);
  const [showCustomRating, setShowCustomRating] = useState(false);
  const [customRating, setCustomRating] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const goToNextScreen = () => {
    if (isFreePlan) {
      router.replace("/signup/onboard-paywall");
    } else {
      router.replace("/signup/onboard-success");
    }
  };

  // Track screen view and check native availability on mount
  useEffect(() => {
    const initializeScreen = async () => {
      try {
        // Track screen view
        FBTrackingService.trackEvent(TRACKING_EVENTS.RATE_US_SCREEN_VIEWED, {
          eventData: {},
        });

        // Detailed diagnostics for store review
        const isAvailable = await StoreReview.isAvailableAsync();
        const hasAction = await StoreReview.hasAction();

        console.log("[RateUs] StoreReview diagnostics:", {
          isAvailableAsync: isAvailable,
          hasAction: hasAction,
          platform: Constants.platform?.ios ? "ios" : "android",
        });

        setNativeAvailable(hasAction);

        // Track native availability
        FBTrackingService.trackEvent(TRACKING_EVENTS.RATE_US_NATIVE_AVAILABLE, {
          eventData: {
            is_available_async: isAvailable,
            has_action: hasAction,
            native_available: hasAction,
            platform: Constants.platform?.ios ? "ios" : "android",
          },
        });

        console.log("[RateUs] Screen initialized", {
          nativeAvailable: hasAction,
          platform: Constants.platform?.ios ? "ios" : "android",
        });

        // If native not available, show custom rating UI after brief delay
        if (!hasAction) {
          setTimeout(() => setShowCustomRating(true), 500);
        }
      } catch (error) {
        console.error("[RateUs] Error initializing screen:", error);
        // Fallback to custom rating on error
        setNativeAvailable(false);
        setShowCustomRating(true);
      }
    };

    initializeScreen();
  }, []);

  const handleRate = async () => {
    try {
      console.log("[RateUs] Rate button clicked", { nativeAvailable });

      if (nativeAvailable) {
        // Show modal IMMEDIATELY - don't block on tracking
        console.log("[RateUs] Calling StoreReview.requestReview()...");
        const result = await StoreReview.requestReview();
        console.log("[RateUs] requestReview() completed, result:", result);

        // Track AFTER modal is shown (fire-and-forget)
        FBTrackingService.trackEvent(TRACKING_EVENTS.RATE_US_BUTTON_CLICKED, {
          eventData: {
            native_available: nativeAvailable,
          },
        });

        // Wait 2 seconds for native prompt to appear before navigating
        setTimeout(() => {
          goToNextScreen();
        }, 2000);
      } else {
        // For non-native path, tracking before UI change is fine
        FBTrackingService.trackEvent(TRACKING_EVENTS.RATE_US_BUTTON_CLICKED, {
          eventData: {
            native_available: nativeAvailable,
          },
        });
        // Show custom rating interface
        setShowCustomRating(true);
      }
    } catch (error) {
      console.error("[RateUs] Error requesting review:", error);

      // Fallback to App Store URL on error
      FBTrackingService.trackEvent(
        TRACKING_EVENTS.RATE_US_FALLBACK_URL_OPENED,
        {
          eventData: {
            reason: "error",
            error: String(error),
          },
        }
      );

      Linking.openURL("https://apps.apple.com/app/id6753935624");
      goToNextScreen();
    }
  };

  const handleCustomRating = (rating: number) => {
    setCustomRating(rating);
    console.log("[RateUs] Custom rating selected:", rating);
  };

  const handleSubmitCustomRating = async () => {
    if (customRating === 0) return;

    setHasSubmitted(true);

    // Track custom rating submission
    FBTrackingService.trackEvent(
      TRACKING_EVENTS.RATE_US_CUSTOM_RATING_SUBMITTED,
      {
        eventData: {
          rating: customRating,
          will_redirect_to_store: customRating >= 4,
        },
      }
    );

    console.log("[RateUs] Custom rating submitted:", customRating);

    if (customRating >= 4) {
      // High rating - direct to App Store
      FBTrackingService.trackEvent(
        TRACKING_EVENTS.RATE_US_FALLBACK_URL_OPENED,
        {
          eventData: {
            reason: "high_custom_rating",
            rating: customRating,
          },
        }
      );

      Linking.openURL("https://apps.apple.com/app/id6753935624");
    }

    // Navigate after short delay
    setTimeout(
      () => {
        goToNextScreen();
      },
      customRating >= 4 ? 1000 : 1500
    );
  };

  const onSkip = () => {
    // Track skip
    FBTrackingService.trackEvent(TRACKING_EVENTS.RATE_US_SKIPPED, {
      eventData: {
        custom_rating_shown: showCustomRating,
        native_available: nativeAvailable,
      },
    });

    console.log("[RateUs] Skipped", {
      customRatingShown: showCustomRating,
      nativeAvailable,
    });

    goToNextScreen();
  };

  return (
    <View style={styles.container}>
      <Header backgroundColor="transparent" showBackButton={false} />
      {/* Sparrow Logo */}
      <View style={styles.logoContainer}>
        <Icons.LogoWithTextBlack width={170} height={38} />
      </View>
      <View style={styles.content}>
        {/* Banner */}
        <View style={styles.bannerContainer}>
          <Image
            source={Images.rateUsBanner}
            style={styles.banner}
            contentFit="cover"
          />
        </View>

        {/* Title & stars */}
        <Text style={styles.title}>
          {showCustomRating
            ? hasSubmitted
              ? customRating >= 4
                ? "Thank you!"
                : "Thanks for your feedback"
              : "How would you rate Sparrow?"
            : "Give us a rating"}
        </Text>

        {/* Custom Rating Stars - Interactive */}
        {showCustomRating && !hasSubmitted ? (
          <View style={styles.starsRow}>
            {[1, 2, 3, 4, 5].map((star) => (
              <TouchableOpacity
                key={star}
                onPress={() => handleCustomRating(star)}
                activeOpacity={0.7}
              >
                <Ionicons
                  name={star <= customRating ? "star" : "star-outline"}
                  size={40}
                  color="#FFB536"
                  style={{ marginHorizontal: 4 }}
                />
              </TouchableOpacity>
            ))}
          </View>
        ) : (
          /* Static Stars */
          <View style={styles.starsRow}>
            {[...Array(5)].map((_, i) => (
              <Ionicons
                key={i}
                name="star"
                size={28}
                color="#FFB536"
                style={{ marginHorizontal: 2 }}
              />
            ))}
          </View>
        )}

        {/* Success message for custom rating */}
        {hasSubmitted && (
          <Text style={styles.successMessage}>
            {customRating >= 4
              ? "Opening the App Store..."
              : "We appreciate your honest feedback!"}
          </Text>
        )}

        {/* Reviews - only show if not in custom rating mode */}
        {!showCustomRating && (
          <View style={styles.reviewsList}>
            {reviews.map((review, idx) => (
              <View
                key={idx}
                style={[styles.reviewCard, { opacity: review.opacity }]}
              >
                <Image
                  source={review.avatar}
                  style={styles.avatar}
                  contentFit="cover"
                />
                <View style={styles.reviewContent}>
                  <View style={styles.reviewStarsRow}>
                    {[...Array(review.stars)].map((_, i) => (
                      <Ionicons
                        key={i}
                        name="star"
                        size={16}
                        color="#FFB536"
                        style={{ marginRight: 1 }}
                      />
                    ))}
                  </View>
                  <Text style={styles.reviewText}>{review.text}</Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Button container */}
      <View style={styles.buttonContainer}>
        {!hasSubmitted && (
          <>
            {showCustomRating ? (
              <ActionButton
                title={customRating >= 4 ? "Rate on App Store" : "Submit"}
                onPress={handleSubmitCustomRating}
                disabled={customRating === 0}
              />
            ) : (
              <ActionButton
                title="Give us a rating"
                onPress={() => router.push("/signup/rate-us-detail")}
              />
            )}
            <TouchableOpacity onPress={onSkip}>
              <Text style={styles.skipBottom}>Skip</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );
};

export default RateUsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
  },
  logoContainer: {
    alignItems: "center",
    paddingTop: 24,
    marginBottom: 10,
  },
  bannerContainer: {
    width: "100%",
    height: 160,
    borderRadius: 18,
    overflow: "hidden",
    marginTop: 0,
    marginBottom: 24,
  },
  banner: {
    width: "100%",
    height: "100%",
  },
  title: {
    fontFamily: FontFamilies.InterTight.Medium,
    fontSize: 30,
    color: "#161D14",
    marginBottom: 8,
    marginTop: 0,
    textAlign: "center",
  },
  starsRow: {
    justifyContent: "center",
    flexDirection: "row",
    marginBottom: 32,
  },
  successMessage: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    color: "#0F9129",
    textAlign: "center",
    marginBottom: 24,
  },
  reviewsList: {
    gap: 16,
    marginBottom: 24,
  },
  reviewCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#EBEBEB",
    borderRadius: 16,
    padding: 16,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 8,
    backgroundColor: "#DDD",
  },
  reviewContent: {
    flex: 1,
  },
  reviewStarsRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  reviewText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    color: "#161D14",
    lineHeight: 20,
  },
  buttonContainer: {
    padding: 16,
    paddingBottom: 32,
    backgroundColor: "transparent",
  },
  skipBottom: {
    color: "#161D14",
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    textAlign: "center",
    marginTop: 12,
    opacity: 0.6,
  },
});
